import React from "react";
import {
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getWeightLengthRechartsScatterProps,
  sortSpeciesForWeightLengthLegend,
} from "../colors";
import { formatTick, formatLengthTick as formatLengthTickUtil } from "../formatters";

export default function WrVsLengthChart({ graphData, selectedUnit, selectedFish, isMobile }) {
  const formatLengthTick = (v) => formatLengthTickUtil(v, selectedUnit.value);
  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };

const uniqueSpeciesWr = sortSpeciesForWeightLengthLegend([
  ...new Set(graphData.map((d) => d.species)),
]);

// --- X Axis (Length): step from legacy padded span; left edge = max(0, dataMin - oneStep) ---
const xValues = graphData.map((d) => Number(d.length || 0));
const xMinVal = xValues.length ? Math.min(...xValues) : 0;
const xMaxVal = xValues.length ? Math.max(...xValues) : 0;

let xStep, xTicks;
const targetTickCountWrLength = 40; // Always generate 40 ticks

if (selectedUnit.value === "English") {
  // English: pick step from legacy padded span, then use only ONE tick-step margin left of data
  const xEnd = xMaxVal + 1;
  const rangeForStep = xEnd - Math.max(0, Math.floor(xMinVal) - 1);
  const xRange = rangeForStep;

  // Determine step size based on the actual range
  if (xRange < 4) {
    xStep = 0.1;
  } else if (xRange < 8) {
    xStep = 0.2;
  } else if (xRange < 12) {
    xStep = 0.3;
  } else if (xRange < 16) {
    xStep = 0.4;
  } else if (xRange < 20) {
    xStep = 0.5;
  } else if (xRange < 24) {
    xStep = 0.6;
  } else if (xRange < 28) {
    xStep = 0.7;
  } else if (xRange < 32) {
    xStep = 0.8;
  } else if (xRange < 36) {
    xStep = 0.9;
  } else {
    xStep = 1.0;
  }

  const xStart = Math.max(0, xMinVal - xStep);

  // Generate ticks from xStart to xEnd
  xTicks = [];
  for (let v = xStart; v <= xEnd + 1e-9; v += xStep) {
    xTicks.push(Number(v.toFixed(2)));
  }

  // If we don't have enough ticks, extend the range
  while (xTicks.length < targetTickCountWrLength) {
    const lastTick = xTicks[xTicks.length - 1];
    xTicks.push(Number((lastTick + xStep).toFixed(2)));
  }
} else {
  // Metric: pick step from legacy padded span, then one tick-step margin on the left
  const xEnd = xMaxVal + 100;
  const rangeForStep = xEnd - Math.max(0, xMinVal - 100);
  const xRange = rangeForStep;

  // Determine step size based on the actual range
  if (xRange < 400) {
    xStep = 10;
  } else if (xRange < 600) {
    xStep = 15;
  } else if (xRange < 800) {
    xStep = 20;
  } else if (xRange < 1000) {
    xStep = 25;
  } else if (xRange < 1200) {
    xStep = 30;
  } else if (xRange < 1400) {
    xStep = 35;
  } else if (xRange < 1600) {
    xStep = 40;
  } else if (xRange < 1800) {
    xStep = 45;
  } else if (xRange < 2000) {
    xStep = 50;
  } else if (xRange < 2200) {
    xStep = 55;
  } else if (xRange < 2400) {
    xStep = 60;
  } else if (xRange < 2600) {
    xStep = 65;
  } else if (xRange < 2800) {
    xStep = 70;
  } else if (xRange < 3000) {
    xStep = 75;
  } else if (xRange < 3200) {
    xStep = 80;
  } else if (xRange < 3400) {
    xStep = 85;
  } else if (xRange < 3600) {
    xStep = 90;
  } else if (xRange < 3800) {
    xStep = 95;
  } else if (xRange < 4000) {
    xStep = 100;
  } else {
    xStep = 200;
  }

  const xStart = Math.max(0, xMinVal - xStep);

  // Generate ticks from xStart to xEnd
  xTicks = [];
  for (let v = xStart; v <= xEnd + 1e-9; v += xStep) {
    xTicks.push(Number(v.toFixed(0))); // No decimals for mm
  }

  // If we don't have enough ticks, extend the range
  while (xTicks.length < targetTickCountWrLength) {
    const lastTick = xTicks[xTicks.length - 1];
    xTicks.push(Number(lastTick + xStep));
  }
}


// --- Y Axis (Wr) ticks: include 1.0; start at 0; step 0.2 normally (>=10 ticks), 0.4 if too many ---
const yValues = graphData.map((d) => Number(d.relativeWeight || 0));
const yMaxVal = yValues.length ? Math.max(...yValues) : 0;
let yStep = 0.2;
const yCandidateUpper = yMaxVal + 0.1; // a little headroom
let estimatedTickCount = Math.ceil(yCandidateUpper / yStep) + 1;
// If many ticks, relax to 0.4
if (estimatedTickCount > 12) {
  yStep = 0.4;
}
// Ensure minimum of 10 ticks when using 0.2 step by extending the upper bound
const minTicks = 10;
const minUpperForMinTicks = (minTicks - 1) * yStep; // e.g., 1.8 for 0.2
let yUpper = Math.max(yCandidateUpper, minUpperForMinTicks);
yUpper = Math.ceil(yUpper / yStep) * yStep; // snap to step
let yTicks = [];
for (let v = 0; v <= yUpper + 1e-9; v += yStep) {
  yTicks.push(Number(v.toFixed(1)));
}
if (!yTicks.includes(1)) yTicks.push(1);
yTicks = Array.from(new Set(yTicks)).sort((a, b) => a - b);
return (
  <ResponsiveContainer {...commonProps}>
    <ScatterChart data={graphData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
      <XAxis
        dataKey="length"
        label={{
          value: `Length (${selectedUnit.value === "English" ? "Inch" : "mm"
            })`,
          position: "outsideBottom",
          offset: isMobile ? 10 : 20,
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        type="number"
        domain={[xTicks[0] || 0, xTicks[xTicks.length - 1] || 0]}
        ticks={xTicks}
        tickFormatter={formatLengthTick}
        angle={isMobile ? -45 : 0}
        textAnchor="middle"
        height={isMobile ? 80 : 60}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
        interval={0}
        allowDataOverflow={true}
        padding={{ right: 10 }}
      />
      <YAxis
        label={{
          value: "Wr",
          angle: -90,
          position: "insideLeft",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 14 : 18,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        domain={[0, yTicks[yTicks.length - 1] || 0]}
        ticks={yTicks}
        tickFormatter={formatTick}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
      />
      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;
          const data = payload[0].payload;

          return (
            <div className="custom-tooltip" style={{
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '12px',
              fontSize: isMobile ? '12px' : '14px',
              minWidth: '200px'
            }}>
              <div style={{
                borderBottom: '2px solid ' + payload[0].color,
                marginBottom: '8px',
                paddingBottom: '4px'
              }}>
                <p style={{
                  margin: '0',
                  fontWeight: 'bold',
                  color: payload[0].color
                }}>
                  {data.species}
                  {data.isStocking ? " · Stocking" : ""}
                </p>
              </div>

              <div style={{ display: 'grid', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>Length:</strong>
                  <span>{data.length.toFixed(2)} {selectedUnit.value === "English" ? "Inch" : "mm"}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong>Relative Weight (Wr):</strong>
                  <span style={{
                    color: data.relativeWeight >= 1.0 ? '#2ecc71' : '#e74c3c',
                    fontWeight: 'bold'
                  }}>
                    {data.relativeWeight.toFixed(3)}
                  </span>
                </div>

                <div style={{
                  marginTop: '4px',
                  padding: '4px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '3px',
                  fontSize: isMobile ? '11px' : '13px',
                  color: data.relativeWeight >= 1.0 ? '#27ae60' : '#c0392b'
                }}>
                  {data.relativeWeight >= 1.0
                    ? '✓ Fish is at or above standard weight'
                    : '⚠ Fish is below standard weight'}
                </div>
              </div>
            </div>
          );
        }}
      />
      <Legend
        layout="horizontal"
        verticalAlign="top"
        align="center"
        wrapperStyle={{
          paddingBottom: isMobile ? 20 : 50,
          fontSize: isMobile ? 12 : 14,
        }}
      />
      <ReferenceLine
        y={1.0}
        stroke="#e74c3c"
        strokeWidth={2}
        strokeDasharray="5 5"
      />
      {uniqueSpeciesWr.map((species) => {
          const samp = graphData.filter((d) => d.species === species && !d.isStocking);
          const stock = graphData.filter((d) => d.species === species && d.isStocking);
          const { fill: c, shape: scatterShape } =
            getWeightLengthRechartsScatterProps(species, uniqueSpeciesWr);
          return (
            <React.Fragment key={species}>
              {samp.length > 0 && (
                <Scatter
                  name={`${species} (${samp.length})`}
                  data={samp}
                  dataKey="relativeWeight"
                  fill={c}
                  shape={scatterShape}
                  fillOpacity={0.6}
                />
              )}
              {stock.length > 0 && (
                <Scatter
                  name={`${species} (stocking) (${stock.length})`}
                  data={stock}
                  dataKey="relativeWeight"
                  fill={c}
                  shape={scatterShape}
                  fillOpacity={0.85}
                  legendType="none"
                />
              )}
            </React.Fragment>
          );
        })}
    </ScatterChart>
  </ResponsiveContainer>
);
}
